export type ColorOption = {
    value: string;
    name: string;
    imageUrl: string;
};

export type ColorSelectorReply = ColorOption[];

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Parses a color-selector user reply.
 *
 * Accepts `unknown` input and returns a validated `ColorSelectorReply` (an array of `ColorOption`)
 * or `null` on any validation failure.
 *
 * Validation rules:
 * - The root must be an array.
 * - The array must not be empty (empty arrays are rejected as invalid replies).
 * - Each element must be a record with string `value`, `name`, and `imageUrl`.
 * - If any element is malformed, the entire reply is rejected (no partial results).
 *
 * Lenient on unknown fields — only the three required fields are validated and extracted.
 * Returns a fresh array of fresh objects containing only the known fields.
 *
 * @param data - The input to parse (expected to be the user message data)
 * @returns A validated `ColorSelectorReply` array, or `null` if validation fails
 */
export function parseColorSelectorReply(data: unknown): ColorSelectorReply | null {
    if (!Array.isArray(data)) {
        return null;
    }

    // Reject empty arrays
    if (data.length === 0) {
        return null;
    }

    const result: ColorOption[] = [];

    for (const item of data) {
        if (!isRecord(item)) {
            return null;
        }

        const { value, name, imageUrl } = item;

        if (typeof value !== 'string' || value === '') {
            return null;
        }
        if (typeof name !== 'string' || name === '') {
            return null;
        }
        if (typeof imageUrl !== 'string' || imageUrl === '') {
            return null;
        }

        // Return a fresh object containing only the known fields
        // (unknown fields from input are not spread through)
        result.push({ value, name, imageUrl });
    }

    return result;
}
